import { puedeDescargar } from "../../../utils/entregarArchivo";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { copiarEnlace, Nodo } from "../../../services/v2/wappdrive.service";
import WappIcono, { TIPOS } from "../../../components/wappIcono";
import Editor from "./editor";
import { DlgCompartir, DlgNombre, DlgVersiones, errorTexto, fecha, peso } from "./dialogos";
import "./wappdrive.css";

type Tipo = "word" | "cell" | "slide" | "pdf";
const APP: Record<Tipo, { nombre: string; nuevo: string | null; ext: "docx" | "xlsx" | "pptx" | null; inicial: string; vacio: string }> = {
	word: { nombre: "WappDocs", nuevo: "Nuevo documento", ext: "docx", inicial: "Documento sin título", vacio: "Todavía no hay documentos. Crea el primero." },
	cell: { nombre: "WappCell", nuevo: "Nueva hoja", ext: "xlsx", inicial: "Hoja sin título", vacio: "Todavía no hay hojas de cálculo. Crea la primera." },
	slide: { nombre: "WappDeck", nuevo: "Nueva presentación", ext: "pptx", inicial: "Presentación sin título", vacio: "Todavía no hay presentaciones. Crea la primera." },
	pdf: { nombre: "WappPDF", nuevo: null, ext: null, inicial: "", vacio: "Todavía no hay PDF. Sube el primero." },
};
const ORDENES: [string, string][] = [["reciente", "Modificados: más nuevos"], ["antiguo", "Modificados: más antiguos"], ["nombre", "Nombre (A–Z)"], ["tamano", "Tamaño"], ["dueno", "Dueño"]];

/**
 * Una app de WappCloud (WappDocs, WappCell, WappDeck, WappPDF; Esteban, 2026-10-09): todos los archivos de ese tipo a los
 * que la persona tiene acceso (suyos, de su equipo, de su área y compartidos), con orden a elegir, crear, subir,
 * compartir, renombrar y abrir en el editor. Los archivos viven en WappDrive; esta es otra puerta de entrada.
 */
const AppOficina: React.FC<{ tipo: Tipo }> = ({ tipo }) => {
	const http = HttpManager.getInstance().wappdriveService;
	const navigate = useNavigate();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusId: number = (legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey]?.id : null) ?? sessionCampus?.id ?? 0;
	const app = APP[tipo];
	const t = TIPOS[tipo];

	const [items, setItems] = useState<Nodo[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [personal, setPersonal] = useState<number | null>(null);
	const [donde, setDonde] = useState<string>("todos");
	const [orden, setOrden] = useState<string>(() => { try { return localStorage.getItem("wc.orden") || "reciente"; } catch (e) { return "reciente"; } });
	const [q, setQ] = useState<string>("");
	const [buscar, setBuscar] = useState<string>("");
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	// Enlace directo (/wappcell?d=123): abre el archivo al entrar, como Google Docs.
	const [editor, setEditor] = useState<number | null>(() => Number(new URLSearchParams(window.location.search).get("d") || 0) || null);
	const [dlg, setDlg] = useState<any>(null);
	const [menu, setMenu] = useState<{ n: Nodo; x: number; y: number } | null>(null);
	const [subiendo, setSubiendo] = useState<number | null>(null);
	const archivoRef = useRef<HTMLInputElement>(null);

	const cargar = async (p = 1) => {
		if (!campusId) return;
		setCargando(true); setError(null);
		try {
			const r = (await http.deTipo(campusId, tipo, buscar, donde, orden, p)).data;
			setItems((prev) => (p === 1 ? r.items : [...prev, ...r.items])); setTotal(r.total); setPage(p); setPersonal(r.personal);
		} catch (e) { setError(errorTexto(e)); } finally { setCargando(false); }
	};
	useEffect(() => { cargar(1); }, [campusId, tipo, donde, orden, buscar]);
	useEffect(() => { try { localStorage.setItem("wc.orden", orden); } catch (e) { /* sin almacenamiento */ } }, [orden]);
	useEffect(() => { const c = () => setMenu(null); window.addEventListener("click", c); return () => window.removeEventListener("click", c); }, []);
	useEffect(() => { const t = setTimeout(() => setBuscar(q.trim()), 350); return () => clearTimeout(t); }, [q]);

	const accion = async (fn: () => Promise<any>, ok?: string) => { setError(null); try { await fn(); if (ok) setAviso(ok); await cargar(1); } catch (e) { setError(errorTexto(e)); } };
	// Descargar a la computadora solo quien puede sacar archivos de WAPP; los demás ya los tienen en WappDrive.
	const [puedeBajar, setPuedeBajar] = useState<boolean>(false);
	useEffect(() => { puedeDescargar().then(setPuedeBajar); }, []);
	const descargar = async (n: Nodo) => { try { const u = (await http.descargar(campusId, n.id, true)).data.url; if (u) window.location.href = u; } catch (e) { setError(errorTexto(e)); } };
	const subir = async (files: File[]) => {
		if (!personal) return;
		for (const f of files) {
			setSubiendo(0);
			try { await http.subir(campusId, personal, null, f, (p) => setSubiendo(p)); } catch (e) { setError(errorTexto(e)); }
		}
		setSubiendo(null); setAviso(files.length > 1 ? files.length + " archivos subidos a Mis archivos." : "Subido a Mis archivos."); cargar(1);
	};

	if (!campusId) return null;
	if (editor) return <Editor campusId={campusId} id={editor} onCerrar={() => { setEditor(null); cargar(1); }} />;

	return (
		<div className="wc">
			<div className="wc-cab">
				<span className="wd-tipo grande" style={{ background: t.fondo, color: t.tinta }}><WappIcono n={t.icono} /></span>
				<div className="wc-tit"><h1>{app.nombre}</h1><span>{total.toLocaleString("es-MX")} {total === 1 ? "archivo" : "archivos"}</span></div>
				<span style={{ flex: 1 }} />
				{app.ext ? <button className="wd-btn pri wc-nuevo" style={{ background: t.tinta, borderColor: t.tinta }} onClick={() => setDlg({ t: "nuevo" })}><WappIcono n="plus" /> {app.nuevo}</button> : null}
				<button className={"wd-btn wc-subir" + (app.ext ? "" : " pri")} style={app.ext ? undefined : { background: t.tinta, borderColor: t.tinta }} onClick={() => archivoRef.current?.click()}><WappIcono n="upload" /> Subir</button>
				<input ref={archivoRef} type="file" multiple hidden accept={tipo === "pdf" ? ".pdf" : tipo === "word" ? ".docx,.doc,.odt,.rtf,.txt" : tipo === "cell" ? ".xlsx,.xls,.ods,.csv" : ".pptx,.ppt,.odp"}
					onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) subir(f); }} />
			</div>

			<div className="wc-filtros">
				<div className="wd-pestanas wc-donde" role="tablist">
					{[["todos", "Todos"], ["mios", "Míos"], ["compartidos", "Compartidos conmigo"]].map(([k, l]) => <button key={k} className={donde === k ? "on" : ""} onClick={() => setDonde(k)}>{l}</button>)}
				</div>
				<div className="wd-busca wc-buscar" role="search">
					<WappIcono n="search" className="wd-busca-lupa" />
					<input type="text" placeholder={"Buscar en " + app.nombre} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setQ("")} aria-label={"Buscar en " + app.nombre} autoComplete="off" spellCheck={false} />
					{q ? <button type="button" className="wd-busca-x" onClick={() => setQ("")} aria-label="Borrar búsqueda"><WappIcono n="x" /></button> : null}
				</div>
				<select className="wc-orden" value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar">{ORDENES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
			</div>

			{error ? <div className="wd-aviso" onClick={() => setError(null)}>{error}</div> : null}
			{aviso ? <div className="wd-aviso ok" onClick={() => setAviso(null)}>{aviso}</div> : null}
			{subiendo !== null ? <div className="wd-aviso ok">Subiendo… {Math.round(subiendo * 100)}%</div> : null}

			{!items.length && !cargando ? <div className="wd-vacio">{buscar ? "Nada con “" + buscar + "”." : app.vacio}</div> : (
				<div className="wd-tabla">
					<div className="wd-fila cab wc-fila"><span>Nombre</span><span className="wd-col-m">Dónde está</span><span className="wd-col-m wd-col-dueno">Dueño</span><span className="wd-col-m">Modificado</span><span className="wd-col-s">Tamaño</span><span /></div>
					{items.map((n) => (
						<div key={n.id} className={"wd-fila wc-fila" + (n.pendiente ? " pendiente" : "")} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenu({ n, x: e.clientX, y: e.clientY }); }}>
							<button className="wd-nombre" onClick={() => (n.pendiente ? setError("Este archivo todavía está pasando de WorkDrive.") : setEditor(n.id))}>
								<span className="wd-tipo" style={{ background: t.fondo, color: t.tinta }}><WappIcono n={t.icono} /></span>
								<span className="wc-nombre"><span className="wd-texto">{n.name.replace(/\.(docx|xlsx|pptx|pdf)$/i, "")}</span><small className="wc-donde-movil">{fecha(n.updated_at)} · {n.ruta}</small></span>
								{n.compartido ? <span className="wd-chip">compartido</span> : null}
							</button>
							<span className="wd-col-m wd-tenue">{n.ruta}</span>
							<span className="wd-col-m wd-col-dueno wd-tenue">{n.owner || n.zoho_creador || "—"}</span>
							<span className="wd-col-m wd-tenue">{fecha(n.updated_at)}{n.updated_by ? " · " + n.updated_by.split(" ")[0] : ""}</span>
							<span className="wd-col-s wd-tenue">{peso(n.size)}</span>
							<span><button className="wd-mas" onClick={(e) => { e.stopPropagation(); const r = (e.target as HTMLElement).getBoundingClientRect(); setMenu({ n, x: r.right, y: r.bottom }); }} aria-label="Acciones"><WappIcono n="dots" /></button></span>
						</div>
					))}
				</div>
			)}
			{items.length < total ? <div className="wc-mas"><button className="wd-btn" disabled={cargando} onClick={() => cargar(page + 1)}>{cargando ? "Cargando…" : "Ver más (" + (total - items.length).toLocaleString("es-MX") + ")"}</button></div> : null}

			{menu ? (
				<div className="wd-menu flotante" style={{ left: Math.min(menu.x, window.innerWidth - 230), top: Math.min(menu.y, window.innerHeight - 330) }} onClick={(e) => e.stopPropagation()}>
					<button onClick={() => { setMenu(null); setEditor(menu.n.id); }}><WappIcono n="edit" /> Abrir en {app.nombre}</button>
					{puedeBajar ? <button onClick={() => { setMenu(null); descargar(menu.n); }}><WappIcono n="download" /> Descargar</button> : null}
					<button onClick={() => { setMenu(null); setDlg({ t: "compartir", n: menu.n }); }}><WappIcono n="share" /> Compartir</button>
					<button onClick={() => { const n = menu.n; setMenu(null); copiarEnlace(n).then(() => setAviso("Enlace copiado.")); }}><WappIcono n="link" /> Copiar enlace</button>
					<button onClick={() => { const n = menu.n; setMenu(null); accion(() => http.copiar(campusId, n.id), "Copia creada."); }}><WappIcono n="copy" /> Hacer una copia</button>
					{menu.n.permiso !== "ver" ? <button onClick={() => { setMenu(null); setDlg({ t: "renombrar", n: menu.n }); }}><WappIcono n="edit" /> Renombrar</button> : null}
					<button onClick={() => { setMenu(null); setDlg({ t: "versiones", n: menu.n }); }}><WappIcono n="history" /> Versiones</button>
					<button onClick={() => { setMenu(null); navigate("/wappdrive?space=" + menu.n.space_id + (menu.n.parent_id ? "&parent=" + menu.n.parent_id : "")); }}><WappIcono n="pin" /> Ir a su carpeta en WappDrive</button>
					{menu.n.permiso !== "ver" ? <button className="peligro" onClick={() => { const n = menu.n; setMenu(null); if (window.confirm("¿Mandar “" + n.name + "” a la papelera?")) accion(() => http.borrar(campusId, n.id), "Enviado a la papelera de WappDrive."); }}><WappIcono n="trash" /> Eliminar</button> : null}
				</div>
			) : null}

			{app.ext ? <button className="wd-fab" style={{ background: t.tinta }} aria-label={app.nuevo || "Nuevo"} onClick={() => setDlg({ t: "nuevo" })}><WappIcono n="plus" /></button> : null}

			{dlg?.t === "nuevo" && app.ext ? (
				<DlgNombre titulo={app.nuevo || ""} inicial={app.inicial} boton="Crear y abrir" onClose={() => setDlg(null)}
					onOk={async (v) => { const r = await http.nuevo(campusId, personal, null, app.ext!, v); setDlg(null); setEditor(r.data.id); }} />
			) : null}
			{dlg?.t === "renombrar" ? <DlgNombre titulo="Renombrar" inicial={dlg.n.name} boton="Guardar" onClose={() => setDlg(null)} onOk={async (v) => { await http.actualizar(campusId, dlg.n.id, { name: v }); setDlg(null); cargar(1); }} /> : null}
			{dlg?.t === "compartir" ? <DlgCompartir campusId={campusId} nodo={dlg.n} onClose={() => { setDlg(null); cargar(1); }} /> : null}
			{dlg?.t === "versiones" ? <DlgVersiones campusId={campusId} nodo={dlg.n} onClose={() => setDlg(null)} onOk={() => { setDlg(null); setAviso("Versión restaurada."); cargar(1); }} /> : null}
		</div>
	);
};

export const WappDocsApp: React.FC = () => <AppOficina tipo="word" />;
export const WappCellApp: React.FC = () => <AppOficina tipo="cell" />;
export const WappDeckApp: React.FC = () => <AppOficina tipo="slide" />;
export const WappPdfApp: React.FC = () => <AppOficina tipo="pdf" />;
export default AppOficina;
